import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { EMPLOYEES, EMPLOYEES_WITH_STATS } from '@/data/employees';
import type { Employee } from '@/types/entities';
import type { EmployeeWithStats } from '@/data/employees';
import type { EmployeeStatsResponse } from '@/types/dashboard';

interface UseEmployeesResult {
  data: readonly Employee[];
  isLoading: boolean;
}

export function useEmployees(): UseEmployeesResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  // Task 135: the switcher wants the org's Employee roster (same list as
  // /team and /employees), not Kanjoy Users. Hits GET /employees — id is
  // an Employee.id, not a Membership.id. If a second hook ever needs
  // Membership.id rows, it should be a separate `useMembers` hook.
  const query = useQuery({
    queryKey: ['employees', orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{
        employees: Array<{
          id: string;
          name: string;
          email: string | null;
          businessUnitId: string | null;
        }>;
      }>(`/api/v1/orgs/${orgId}/employees`, token);
      return res.employees.map(
        (e): Employee => ({
          id: e.id,
          name: e.name,
          // 135: Employees are not Memberships and don't carry a role.
          // 'member' is a safe placeholder for the switcher, which drops
          // the row's secondary-line display in D2.
          role: 'member',
          businessUnitId: e.businessUnitId ?? '',
          email: e.email ?? '',
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: EMPLOYEES, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

interface UseEmployeesWithStatsResult {
  data: readonly EmployeeWithStats[];
  isLoading: boolean;
}

export function useEmployeesWithStats(): UseEmployeesWithStatsResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['employeeStats', orgId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      const res = await apiFetch<EmployeeStatsResponse>(
        `/api/v1/orgs/${orgId}/dashboard/employees/stats${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      return res.employees.map(
        (e): EmployeeWithStats => ({
          id: e.id,
          name: e.name,
          role: 'member',
          businessUnitId: '',
          email: '',
          totalHours: e.totalHours,
          billableHours: 0,
          generalHours: 0,
          events: 0,
          // Task 113 D4 — round to whole percent for the /team member-card
          // "% billable" label (mirrors useDashboardData for /dashboard).
          billablePercent:
            e.avgBillablePerDay > 0
              ? Math.round((e.avgBillablePerDay / e.avgHoursPerDay) * 100)
              : 0,
          fullDays: e.fullDays,
          halfDays: e.halfDays,
          checkIns: e.checkIns,
          effectiveDays: e.effectiveDays,
          avgHoursDay: e.avgHoursPerDay,
          avgBillableDay: e.avgBillablePerDay,
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: EMPLOYEES_WITH_STATS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}
